import dayjs from 'dayjs';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import imgBgL1 from '../../assets/images/common/clock_bg_l_1.png';
import imgBgL2 from '../../assets/images/common/clock_bg_l_2.png';
import imgBgL3 from '../../assets/images/common/clock_bg_l_3.png';
import imgBgL4 from '../../assets/images/common/clock_bg_l_4.png';
import imgBgL5 from '../../assets/images/common/clock_bg_l_5.png';
import imgBgD1 from '../../assets/images/common/clock_bg_d_1.png';
import imgBgD2 from '../../assets/images/common/clock_bg_d_2.png';
import imgBgD3 from '../../assets/images/common/clock_bg_d_3.png';
import imgBgD4 from '../../assets/images/common/clock_bg_d_4.png';
import imgBgD5 from '../../assets/images/common/clock_bg_d_5.png';
import { bScreenMode, themeState } from '../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useFetchHolidays } from '../../hooks/useFetchHolidays';

const AnalogClockRoot = styled.div`
  /* 시계 위젯 전용 환경 변수 */
  --clock-size: 254px;
  --inner-circle-size: 122px;

  &.view-s {
    --clock-size: 360px;
    --inner-circle-size: 180px;

    &.size-xs {
      --clock-size: 160px;
      --inner-circle-size: 60px;
    }

    &.size-sm {
      --clock-size: 248px;
      --inner-circle-size: 110px;
    }
  }

  --clock-outer-drop-shadow: 4px 4px 12px 0px rgba(0, 0, 0, 0.15) inset;
  --clock-inner-drop-shadow: drop-shadow(4px 4px 12px rgba(0, 0, 0, 0.15)) drop-shadow(-4px -4px 6px rgba(255, 255, 255, 0.5));
  --small-center-circle-bg: radial-gradient(70.89% 70.89% at 35.71% 32.14%, #666 0%, #000 100%);
  --small-center-circle-shadow: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.15));
  --hour-mark-bg: #323232;
  --minute-mark-bg: #888;

  html[data-theme='dark'] & {
    --clock-outer-drop-shadow: 4px 4px 12px 0px rgba(0, 0, 0, 0.5) inset;
    --clock-inner-drop-shadow: drop-shadow(4px 4px 12px rgba(0, 0, 0, 0.8)) drop-shadow(-4px -4px 12px rgba(105, 105, 105, 0.3));
    --small-center-circle-bg: radial-gradient(70.89% 70.89% at 35.71% 32.14%, #fff 0%, #b7b7b7 100%);
    --small-center-circle-shadow: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.15));
    --hour-mark-bg: #ededed;
    --minute-mark-bg: #afafaf;
  }

  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: var(--clock-bg-color);
  gap: 0.5rem;

  &.view-s {
    gap: 1.5rem;

    .hour-hand {
      width: 12px;
      height: 104px;
      margin-left: -6px;
    }

    .minute-hand {
      width: 7px;
      height: 126px;
    }

    .second-hand {
      width: 5px;
      height: 138px;
      margin-left: -2px;
    }

    .clock-face-inner {
      background: var(--widget-bg);
    }

    &.size-xs {
      gap: 0.25rem;

      .hour-hand {
        width: 8px;
        height: 46px;
        margin-left: -5px;
      }

      .minute-hand {
        width: 6px;
        height: 58px;
      }

      .second-hand {
        width: 4px;
        height: 64px;
      }

      .clock-time-wrap {
        padding: 0;
      }

      .clock-time {
        font-size: 1.75rem;
      }
    }

    &.size-sm {
      gap: 1rem;

      .hour-hand {
        width: 8px;
        height: 68px;
        margin-left: -5px;
      }

      .minute-hand {
        width: 6px;
        height: 86px;
      }

      .second-hand {
        width: 4px;
        height: 104px;
      }

      .clock-time-wrap {
        padding: 0;
      }

      .clock-time {
        font-size: 2.75rem;
      }
    }
  }

  .clock-container {
    position: relative;
    width: var(--clock-size);
    height: var(--clock-size);
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .clock-face {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .hour-marks {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .hour-mark {
    position: absolute;
    left: 50%;
    transform-origin: 0 calc(var(--clock-size) / 2);
  }

  .minute-mark {
    position: absolute;
    left: 50%;
    transform-origin: 0 calc(var(--clock-size) / 2);
  }

  .hour-number {
    position: absolute;
    text-align: center;
    transform-origin: center;
  }

  .hand {
    position: absolute;
    transform-origin: bottom center;
    bottom: calc(var(--clock-size) / 2);
    left: calc(var(--clock-size) / 2);
  }

  & {
    .clock-face {
      border-radius: 50%;
      /* background: var(--board); */
      box-shadow: var(--clock-outer-drop-shadow);
      // background: linear-gradient(145deg, #d1d3dd, var(--board));
      // box-shadow: 20px 20px 60px #c5c7d1, -20px -20px 60px var(--board);
    }

    .clock-face-inner {
      width: var(--inner-circle-size);
      height: var(--inner-circle-size);
      border-radius: 50%;
      z-index: 1;
      background: var(--board);
      filter: var(--clock-inner-drop-shadow);
      // box-shadow: 20px 20px 40px #b0b2bb, -20px -20px 40px var(--board);
    }

    .clock-center {
      position: absolute;
      width: 15px;
      height: 15px;
      border-radius: 50%;
      top: calc(calc(var(--clock-size) / 2) - 7.5px);
      left: calc(calc(var(--clock-size) / 2) - 7.5px);
      background: var(--small-center-circle-bg);
      box-shadow: var(--small-center-circle-shadow);

      z-index: 50;
    }

    .hour-mark {
      width: 2px;
      height: 24px;
    }

    .minute-mk {
      width: 1px;
      height: 16px;
    }

    .hour-mark::after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      width: 2px;
      height: 16px;
      border-radius: 10px;
      background: var(--board);
      box-shadow: inset 1px 1px 7px #bec0ca, inset -1px -1px 7px var(--board);
    }

    .minute-mk::after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      width: 1px;
      height: 8px;
      border-radius: 10px;
      background: var(--board);
      box-shadow: inset 1px 1px 7px #bec0ca, inset -1px -1px 7px var(--board);
    }

    .minute-mark {
      display: none;
    }

    .hour-number {
      display: none;
    }

    .hour-hand {
      width: 8px;
      height: 74px;
      background: var(--hour-mark-bg);
      border-radius: 20px;
      z-index: 7;
      margin-left: -4px;
      z-index: 30;
    }

    .minute-hand {
      width: 6px;
      height: 92px;
      background: var(--minute-mark-bg);
      border-radius: 20px;
      z-index: 8;
      margin-left: -3px;
      z-index: 20;
    }

    .second-hand {
      width: 4px;
      height: 108px;
      background: var(--primary);
      border-radius: 20px;
      z-index: 9;
      margin-left: -1px;
      z-index: 40;
    }

    .clock-time-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      width: 100%;
      padding: 10px 0;
      position: relative;
      color: var(--widget-text-primary);
      z-index: 10;

      .clock-time {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 3rem;
        font-weight: 800;
        line-height: 1.2;

        > span {
          font-size: 1rem;
        }
      }

      .date {
        font-size: 1rem;
      }
    }
  }
`;

/* 현재 시간 애니메이션 */
const setClockInterval = ({
  ref,
  setTime,
  type,
  clockId,
}: {
  ref: MutableRefObject<number>;
  setTime: Dispatch<SetStateAction<{ currentTime: { h: string; m: string; s: string }; ampm: string }>>;
  type: 'normal' | 'smooth';
  clockId: string;
}) => {
  const hourHand = document.querySelectorAll(`[data-clock-id="${clockId}"] .hour-hand`) as NodeListOf<HTMLDivElement>;
  const minuteHand = document.querySelectorAll(`[data-clock-id="${clockId}"] .minute-hand`) as NodeListOf<HTMLDivElement>;
  const secondHand = document.querySelectorAll(`[data-clock-id="${clockId}"] .second-hand`) as NodeListOf<HTMLDivElement>;

  const now = new Date();
  const hours24 = now.getHours();
  const hours = hours24 % 12 || 12;
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const ms = now.getMilliseconds();
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  const currentTime = { h: hours.toString().padStart(2, '0'), m: minutes.toString().padStart(2, '0'), s: seconds.toString().padStart(2, '0') };
  // const currentTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  let hourDegrees = 0;
  let minuteDegrees = 0;
  let secondDegrees = 0;

  if (type === 'smooth') {
    /* 부드러운 애니메이션 */
    hourDegrees = hours * 30 + (30 / 60) * minutes;
    minuteDegrees = minutes * 6 + (6 / 60) * seconds + (6 / (60 * 1000)) * ms;
    secondDegrees = seconds * 6 + (6 / 1000) * ms;
  } else {
    /* 초단위 애니메이션 */
    hourDegrees = (hours % 12) * 30 + minutes * 0.5;
    minuteDegrees = minutes * 6 + seconds * 0.1;
    secondDegrees = seconds * 6;
  }

  hourHand &&
    hourHand.forEach((el: HTMLDivElement) => {
      el.style.transform = `rotate(${hourDegrees}deg)`;
    });
  minuteHand &&
    minuteHand.forEach((el: HTMLDivElement) => {
      el.style.transform = `rotate(${minuteDegrees}deg)`;
    });
  secondHand &&
    secondHand.forEach((el: HTMLDivElement) => {
      el.style.transform = `rotate(${secondDegrees}deg)`;
    });

  setTime({ currentTime, ampm });

  ref.current = requestAnimationFrame(() => setClockInterval({ ref, setTime, type, clockId }));
};

interface IProps {
  viewType: 'd' | 's'; // d : 대시보드, s : 상황판
  size?: 'xs' | 'sm' | 'md' | 'lg'; // 상황판 사이즈 - xs : ( 가로 1, 세로 1 ), sm : ( 가로 1, 세로 2 ), md : ( 가로 2, 세로 2 ), lg : ( 가로 2, 세로 3 )
}

/* 아날로그 시계 위젯 ( 가로 1 * 세로 2 ) */
export const AnalogClock = ({ viewType, size = 'lg' }: IProps) => {
  const HOUR_LENGTH = 12; // 시계 시침 개수 [ 시침 = 12, 분침포함 = 60 ]
  const MARK_LIMIT = 1; // 시침과 분침 구분 [ 시침 = 1, 분침 = 5 ]
  const curTimeFmt = dayjs().locale('en').format(`YY.MM.DD ddd`); // 현재시간 영어권 형식
  const [time, setTime] = useState<{ currentTime: { h: string; m: string; s: string }; ampm: string }>({ currentTime: { h: '00', m: '00', s: '00' }, ampm: 'AM' });
  const animateFrameRef = useRef<number>(0);
  const clockId = useRef(`analog-clock-${Math.random().toString(36).substring(2, 9)}`).current;

  useEffect(() => {
    setClockInterval({ ref: animateFrameRef, setTime, type: 'smooth', clockId });

    animateFrameRef.current = requestAnimationFrame(() => setClockInterval({ ref: animateFrameRef, setTime, type: 'smooth', clockId }));

    // setClockInterval();
    // const interval = setInterval(setClockInterval, 1000);

    return () => {
      cancelAnimationFrame(animateFrameRef.current);
      // clearInterval(interval);
    };
  }, []);

  return (
    <AnalogClockRoot className={`view-${viewType} size-${size}`} data-clock-id={clockId}>
      <div className='clock-container'>
        <div className='clock-face-inner' />
        <div className='clock-face' />
        <div className='hour-marks'>
          {Array.from({ length: HOUR_LENGTH }, (_, i) => (
            <div key={i + 1} className={`${i % MARK_LIMIT !== 0 ? 'minute-mk' : 'hour-mark'}`} style={{ transform: `rotate(${i * (360 / HOUR_LENGTH)}deg)` }} />
          ))}
        </div>
        <div className='hour-hand hand' />
        <div className='minute-hand hand' />
        <div className='second-hand hand' />
        <div className='clock-center' />
      </div>

      <div className='clock-time-wrap'>
        <div className='clock-time'>
          {time.currentTime.h}:{time.currentTime.m}:{time.currentTime.s}
          <span>{time.ampm}</span>
        </div>
        <div className='date'>{curTimeFmt}</div>
      </div>
    </AnalogClockRoot>
  );
};

const DigitalClockRoot = styled.div`
  --opacity100: 0.75;

  html[data-theme='dark'] & {
    --opacity100: 1;
  }

  position: relative;
  background: var(--board);
  height: 100%;

  &.view-s {
    .clock-time-wrap {
      gap: 2.25rem;

      .clock-time-inner {
        .ampm {
          font-size: 2.25rem;
        }

        .clock-time {
          font-size: 7rem;
        }
      }

      .date {
        font-size: 1.75rem;
      }
    }

    &.size-sm {
      .clock-time-wrap {
        gap: 1rem;

        .clock-time-inner {
          .ampm {
            font-size: 1.75rem;
          }

          .clock-time {
            font-size: 4rem;
          }
        }

        .date {
          font-size: 1.25rem;
        }
      }
    }

    &.size-xs {
      .clock-time-wrap {
        gap: 1rem;

        .clock-time-inner {
          .ampm {
            font-size: 1.75rem;
          }

          .clock-time {
            font-size: 4rem;
          }
        }

        .date {
          font-size: 1.25rem;
        }
      }
    }
  }

  > figure {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    > img {
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;

      &.opacity-0 {
        opacity: 0;
        transition: opacity 0.3s ease-in;
      }

      &.opacity-100 {
        opacity: var(--opacity100);
        transition: opacity 0.3s ease-out;
      }
    }
  }

  .clock-time-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    height: 100%;
    position: relative;
    color: var(--text-primary);
    z-index: 10;

    .clock-time-inner {
      display: flex;
      flex-direction: column;
      align-items: center;

      .ampm {
        font-size: 1.75rem;
      }

      .clock-time {
        display: flex;
        align-items: center;
        font-size: 4.25rem;
        font-weight: 800;
        line-height: 1.2;
        text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.2);
      }
    }

    .date {
      font-size: 1.25rem;
      font-weight: 400;
    }
  }

  .clock-btn-wrap {
    position: absolute;
    width: 100%;
    height: 100%;

    button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: auto;
      height: auto;
      margin: 0;
      padding: 0;
      color: var(--text-color);
      z-index: 20;

      &.prev-btn {
        left: 10px;
      }

      &.next-btn {
        right: 10px;
      }

      &:hover {
        color: #fff;
      }
    }
  }
`;

const getCurrentBgIdx: number = Number(localStorage.getItem('clock-bg-idx')) || 0;

/* 디지털 시계 위젯 ( 가로 1 * 세로 2 ) */
export const DigitalClock = ({ viewType, size = 'lg' }: IProps) => {
  const dTheme = useRecoilValue(themeState); // 대시보드 테마
  const sTheme = useRecoilValue(bScreenMode); // 상황판 테마
  const isLight = viewType === 's' ? sTheme === '1' : dTheme === 'light'; // 라이트 모드 여부
  const curTimeFmt = dayjs().locale('ko').format(`YY.MM.DD dddd`); // 현재시간 영어권 형식
  const [time, setTime] = useState<{ currentTime: { h: string; m: string; s: string }; ampm: string }>({ currentTime: { h: '00', m: '00', s: '00' }, ampm: 'AM' });
  const [isLoading, setIsLoading] = useState(true);
  const [currentBgIdx, setCurrentBgIdx] = useState<number>(getCurrentBgIdx);
  const imgs = isLight ? [imgBgL5, imgBgL1, imgBgL2, imgBgL3, imgBgL4] : [imgBgD1, imgBgD2, imgBgD3, imgBgD4, imgBgD5];
  const [imgBgs, setImgBgs] = useState<string[]>(imgs);
  const [opacity, setOpacity] = useState('opacity-0');
  const animateFrameRef = useRef<number>(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const clockId = useRef(`digital-clock-${Math.random().toString(36).substring(2, 9)}`).current;

  useEffect(() => {
    isLoading ? setOpacity('opacity-0') : setOpacity('opacity-100');
  }, [isLoading, currentBgIdx]);

  useEffect(() => {
    setClockInterval({ ref: animateFrameRef, setTime, type: 'smooth', clockId });

    animateFrameRef.current = requestAnimationFrame(() => setClockInterval({ ref: animateFrameRef, setTime, type: 'smooth', clockId }));

    return () => cancelAnimationFrame(animateFrameRef.current);
  }, []);

  useEffect(() => {
    if (viewType === 'd') {
      const imgAll = imgRef.current?.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
      imgAll?.forEach((el) => el.classList.remove('opacity-100'));
      setImgBgs(imgs);
    }
  }, [dTheme]);

  const changeFadeImg = (type: 'prev' | 'next') => {
    const imgAll = imgRef.current?.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
    imgAll?.forEach((el) => el.classList.remove('opacity-100'));

    const curIdx = type === 'prev' ? (currentBgIdx === 0 ? imgBgs.length - 1 : currentBgIdx - 1) : currentBgIdx === imgBgs.length - 1 ? 0 : currentBgIdx + 1;
    setCurrentBgIdx(curIdx);
    localStorage.setItem('clock-bg-idx', curIdx.toString());
  };

  return (
    <DigitalClockRoot className={`view-${viewType} size-${size}`} data-clock-id={clockId}>
      <figure ref={imgRef}>
        {imgBgs.map((el: string, idx: number) => (
          <img key={el} src={el} alt='시계위젯 배경' loading='lazy' onLoad={() => setIsLoading(false)} className={`${idx === currentBgIdx ? opacity : 'opacity-0'}`} />
        ))}
      </figure>
      <div className='clock-time-wrap'>
        <div className='clock-time-inner'>
          <span className='ampm'>{time.ampm === 'AM' ? '오전' : '오후'}</span>
          <span className='clock-time'>
            {time.currentTime.h}:{time.currentTime.m}:{time.currentTime.s}
          </span>
        </div>
        <div className='date'>{curTimeFmt}</div>
      </div>
      {viewType === 'd' && (
        <div className='clock-btn-wrap'>
          <button type='button' onClick={() => changeFadeImg('prev')} className='prev-btn'>
            <span className='material-symbols-rounded'>chevron_left</span>
          </button>
          <button type='button' onClick={() => changeFadeImg('next')} className='next-btn'>
            <span className='material-symbols-rounded'>chevron_right</span>
          </button>
        </div>
      )}
    </DigitalClockRoot>
  );
};

const ClockCalendarRoot = styled.div`
  --clock-bg: #fff;
  --clock-sub-bg: var(--clock-bg);
  --clock-hand: #242424;
  --calendar-bg: #e9f2ff;
  --clock-size: 320px;
  --outer-shadow: 2px 2px 16px 0 rgba(0, 0, 0, 0.15);
  --inner-shadow: -2px 2px 12px 0 rgba(0, 0, 0, 0.25) inset;
  --marker-distance: calc(var(--clock-size) * 0.43);
  --marker-minute-distance: calc(var(--clock-size) * 0.46);

  html[data-theme='dark'] & {
    --clock-bg: #2f2f2f;
    --clock-sub-bg: #242424;
    --clock-hand: #fafafa;
    --calendar-bg: #162539;
  }

  &.view-s {
    --clock-size: 450px;

    &.size-xs {
      --clock-size: 200px;
    }
  }

  @media screen and (max-width: 768px) {
    & {
      .mobile-btns-wrap {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        display: flex !important;
        justify-content: space-between;
        align-items: center;

        button {
          position: absolute;
          width: 10px;
          padding: 0 0.5rem;
          height: auto;
          z-index: 10;

          &:hover {
            background: transparent;
          }

          &.left-btn {
            left: 5px;
          }

          &.right-btn {
            right: 5px;
          }

          span {
            color: var(--text-primary);
          }
        }
      }

      .clock-wrap,
      .calendar-wrap {
        display: none !important;
        width: 100% !important;
      }

      .clock-wrap.active {
        display: flex !important;
      }

      .calendar-wrap.active {
        display: flex !important;
      }

      /*
      .calendar-wrap {
        display: none !important;
      }
      */
    }
  }

  @media screen and (max-width: 480px) {
    --clock-size: 75dvw;

    .hour-hand {
      height: calc(var(--clock-size) - 60dvw) !important;
    }

    .minute-hand {
      height: calc(var(--clock-size) - 55dvw) !important;
    }

    .second-hand {
      height: calc(var(--clock-size) - 50dvw) !important;
    }
  }

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row !important;
  gap: 0.5rem;
  width: 100%;
  height: 100%;
  padding: 1rem;
  background: var(--board);
  color: #fff;

  .mobile-btns-wrap {
    display: none;
  }

  &.view-s {
    .calendar-days {
      > div {
        span {
          width: 50px;
          height: 50px;
        }
      }
    }

    .clock-container {
      .hand {
        width: 24px;
        margin-left: -12px;
      }

      .hour-hand {
        height: 100px;
      }

      .minute-hand {
        height: 160px;
      }

      .small-circle {
        width: 40px;
        height: 40px;
        margin-left: -20px;
        margin-bottom: -20px;
      }

      .clock-time {
        span {
          font-size: 3rem;
        }
      }
    }

    &.size-xs {
      .calendar-wrap {
        .calendar-header {
          .date-title {
            font-size: 1.25rem;
            padding: 0 5px;
          }
        }

        .calendar-body {
          height: calc(100% - 24px);
          font-size: 0.875rem;

          .calendar-week-day {
            height: 24px;
          }

          .calendar-days {
            height: calc(100% - 24px);
          }
        }
      }
      .calendar-days {
        > div {
          span {
            width: 24px;
            height: 24px;
          }
        }
      }

      .clock-container {
        .hand {
          width: 14px;
          margin-left: -7px;
        }

        .hour-hand {
          height: 44px;
        }

        .minute-hand {
          height: 70px;
        }

        .small-circle {
          width: 26px;
          height: 26px;
          margin-left: -13px;
          margin-bottom: -13px;
        }

        .clock-time {
          span {
            font-size: 1.5rem;
          }
        }
      }
    }
  }

  .clock-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(50% - 0.25rem);
    height: 100%;
  }

  .clock-container {
    position: relative;
    width: var(--clock-size);
    height: var(--clock-size);
    border-radius: 50%;
    overflow: hidden;
    pointer-events: auto;
    background: var(--clock-bg);
    box-shadow: var(--outer-shadow);

    &:after {
      content: '';
      position: absolute;
      left: 5px;
      top: 5px;
      width: calc(var(--clock-size) - 10px);
      height: calc(var(--clock-size) - 10px);
      border-radius: 50%;
      background: var(--clock-sub-bg);
      box-shadow: var(--inner-shadow);
      z-index: 1;
    }

    .marks {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      z-index: 2;

      .hour-mark {
        position: absolute;
        display: flex;

        span {
          color: var(--text-primary);
          font-size: 1.25rem;
        }
      }

      .minute-mark {
        position: absolute;
        width: 1px;
        height: 1px;
        background-color: #666;
      }
    }

    .hour-number {
      display: none;
    }

    .hand {
      position: absolute;
      transform-origin: bottom center;
      left: calc(var(--clock-size) / 2);
      bottom: calc(var(--clock-size) / 2);
      width: 2px;
      border-radius: 20px;
      margin-left: -1px;
    }

    .hour-hand {
      width: 6px;
      height: 80px;
      background-color: var(--clock-hand);
      margin-left: -3px;
      z-index: 20;
    }

    .minute-hand {
      width: 5px;
      height: 110px;
      background-color: var(--clock-hand);
      margin-left: -2.5px;
      z-index: 10;
    }

    .second-center-circle {
      position: absolute;
      width: 12px;
      height: 12px;
      left: calc(var(--clock-size) / 2);
      bottom: calc(var(--clock-size) / 2);
      margin-left: -6px;
      margin-bottom: -6px;
      background: var(--primary);
      border-radius: 50%;
      z-index: 35;
    }

    .second-hand {
      height: 127px;
      background-color: var(--primary);
      filter: drop-shadow(0 0 5px rgba(79, 108, 253, 0.5));
      z-index: 35;

      .second-end-bar {
        position: absolute;
        bottom: -16px;
        width: 6px;
        height: 16px;
        border-radius: 3px;
        margin-left: -2px;
        background: var(--primary);
      }
    }

    .small-circle {
      position: absolute;
      width: 24px;
      height: 24px;
      left: calc(var(--clock-size) / 2);
      bottom: calc(var(--clock-size) / 2);
      margin-left: -12px;
      margin-bottom: -12px;
      // background: var(--clock-small-bg);
      background: rgba(217, 217, 217, 0.15);
      backdrop-filter: blur(1px);
      border-radius: 50%;
      z-index: 30;
    }

    .clock-time {
      position: absolute;
      width: 100%;
      bottom: 15%;
      left: 0;
      text-align: center;
      z-index: 30;

      span {
        font-size: 2.25rem;
        font-weight: 800;
        color: var(--primary);
        text-shadow: 2px 2px 4px rgba(77, 107, 163, 0.2);
      }
    }
  }

  .calendar-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: calc(50% - 0.25rem);
    height: 100%;
    overflow: hidden;
  }

  .calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    .date-title {
      font-size: 1.75rem;
      font-weight: 700;
      padding: 5px 10px;
      color: var(--primary);
    }

    .date-control {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: var(--text-primary);

      button {
        width: auto;
        height: auto;
        padding: 0;

        &:hover {
          > span.material-symbols-rounded {
            color: #fff;
          }
        }

        > span.material-symbols-rounded {
          color: var(--text-primary);
        }
      }

      .today-btn {
        padding: 2px 4px;
        border-radius: 6px;
        cursor: pointer;

        &:hover {
          background-color: var(--outline);
        }
      }
    }
  }

  .calendar-body {
    width: 100%;
    height: calc(100% - 48px);

    .calendar-week-day {
      height: 50px;
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      font-weight: 600;

      div {
        display: grid;
        place-items: center;
        color: #888;

        &.sunday {
          color: #f00;
        }
      }
    }
  }

  .calendar-days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    width: 100%;
    height: calc(100% - 50px);
    color: var(--color-txt);

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: auto;
      position: relative;
      animation: to-top 1s forwards;

      &.today {
        span {
          // display: none;
          background-color: var(--primary);
          border-radius: 50%;
          color: #fff;
        }
      }

      &.sunday {
        span {
          color: #f00;
        }
      }

      &.holiday {
        span {
          color: #f00;
        }

        span.holiday-name {
          display: none;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: var(--board);
          font-size: 12px;
          text-align: center;
          line-height: 1.4;
          z-index: 10;
        }

        &:hover {
          span.holiday-name {
            display: flex;
          }
        }
      }

      span {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 34px;
        height: 34px;
        color: var(--text-primary);
        font-weight: 600;
      }
    }
  }

  .year-change {
    height: 40px;
    width: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    margin: 0 10px;
    cursor: pointer;
  }

  .year-change:hover {
    background-color: var(--color-hover);
  }

  .month-list {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-color: var(--bg-main);
    padding: 20px;
    grid-template-columns: repeat(3, auto);
    gap: 5px;
    display: grid;
    transform: scale(1.5);
    visibility: hidden;
    pointer-events: none;
  }

  .month-list.show {
    transform: scale(1);
    visibility: visible;
    pointer-events: visible;
    transition: all 0.2s ease-in-out;
  }

  .month-list > div {
    display: grid;
    place-items: center;
  }

  .month-list > div > div {
    width: 100%;
    padding: 5px 20px;
    border-radius: 10px;
    text-align: center;
    cursor: pointer;
    color: #fff;
  }

  @keyframes to-top {
    0% {
      transform: translateY(100%);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

/* 시계 및 캘린더 위젯 ( 가로 2 * 세로 2 ) */
export const ClockCalendar = ({ viewType, size = 'lg' }: IProps) => {
  const date: Date = new Date();
  const HOUR_LENGTH = 12; // 시계 시침 개수 [ 시침 = 12, 분침포함 = 60 ]
  const MINUTE_LENGTH = 60;
  const MARK_LIMIT = 5; // 시침과 분침 구분 [ 시침 = 1, 분침 = 5 ]
  const [time, setTime] = useState<{ currentTime: { h: string; m: string; s: string }; ampm: string }>({ currentTime: { h: '00', m: '00', s: '00' }, ampm: 'AM' });
  const [curDate, setCurDate] = useState<{ year: number; month: number }>({ year: date.getFullYear(), month: date.getMonth() }); // 현재 날짜 연도, 월
  const [firstDay, setFirstDay] = useState<Date>(new Date(curDate.year, curDate.month, 1)); // 현재 날짜와 선택된 날짜의 첫번째 일
  const [isClock, setIsClock] = useState<boolean>(true); // 모바일에서 시게 - 캘린더 전환 여부
  const { holidays, isLoading: holidaysLoading, isError: holidaysError } = useFetchHolidays(curDate.year.toString());
  const [holidaysList, setHolidaysList] = useState<any[]>([]);
  const monthNames: string[] = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
  const weekNames: string[] = ['일', '월', '화', '수', '목', '금', '토'];
  // const monthNames: string[] = ['1월 Jan', '2월 Feb', '3월 Mar', '4월 Apr', '5월 May', '6월 Jun', '7월 Jul', '8월 Aug', '9월 Sep', '10월 Oct', '11월 Nov', '12월 Dec'];
  // const weekNames: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isLeapYear = (year: number) => (year % 4 === 0 && year % 100 !== 0 && year % 400 !== 0) || (year % 100 === 0 && year % 400 === 0);
  const getFebDays = (year: number) => (isLeapYear(year) ? 29 : 28);
  const daysOfMonth: number[] = [31, getFebDays(curDate.year), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  const animateFrameRef = useRef<number>(0);
  const monthPickerRef = useRef<HTMLDivElement>(null);
  const clockId = useRef(`clock-calendar-${Math.random().toString(36).substring(2, 9)}`).current;

  useEffect(() => {
    if (!holidaysLoading && !holidaysError && holidays && holidays.length > 0) {
      const curMonthFilter = holidays.filter((holiday) => holiday.month === curDate.month + 1) || [];
      setHolidaysList(curMonthFilter);
    } else {
      setHolidaysList([]);
    }
  }, [holidays, holidaysLoading, holidaysError, curDate]);

  useEffect(() => {
    setClockInterval({ ref: animateFrameRef, setTime, type: 'smooth', clockId });
    animateFrameRef.current = requestAnimationFrame(() => setClockInterval({ ref: animateFrameRef, setTime, type: 'smooth', clockId }));

    return () => {
      cancelAnimationFrame(animateFrameRef.current);
    };
  }, []);

  // 월 피커 클랙 이벤트
  const onClickMonthPicker = () => {
    monthPickerRef.current && monthPickerRef.current?.classList.add('show');
  };

  // 월 피커 - 월 클릭시 변경 이벤트
  const onClickMonthChange = (month: number) => {
    setCurDate((prev) => ({ ...prev, month }));
    setFirstDay(new Date(curDate.year, month, 1));
    monthPickerRef.current && monthPickerRef.current?.classList.remove('show');
  };

  // 연도 및 월 변경 이벤트
  const onClickChangeDate = (type: 'prev' | 'next' | 'today') => {
    if (type === 'today') {
      setCurDate({ year: date.getFullYear(), month: date.getMonth() });
      setFirstDay(new Date(date.getFullYear(), date.getMonth(), 1));
    } else if (type === 'prev') {
      const setCurrentMonth = curDate.month - 1 < 0 ? 11 : curDate.month - 1;
      const setCurrentYear = curDate.month - 1 < 0 ? curDate.year - 1 : curDate.year;
      setCurDate({ year: setCurrentYear, month: setCurrentMonth });
      setFirstDay(new Date(setCurrentYear, setCurrentMonth, 1));
    } else if (type === 'next') {
      const setCurrentMonth = curDate.month + 1 > 11 ? 0 : curDate.month + 1;
      const setCurrentYear = curDate.month + 1 > 11 ? curDate.year + 1 : curDate.year;
      setCurDate({ year: setCurrentYear, month: setCurrentMonth });
      setFirstDay(new Date(setCurrentYear, setCurrentMonth, 1));
    }
  };

  return (
    <ClockCalendarRoot className={`view-${viewType} size-${size}`} data-clock-id={clockId}>
      <div className='mobile-btns-wrap'>
        {isClock ? (
          <button type='button' className='right-btn' onClick={() => setIsClock(false)}>
            <span className='material-symbols-rounded'>chevron_right</span>
          </button>
        ) : (
          <button type='button' className='left-btn' onClick={() => setIsClock(true)}>
            <span className='material-symbols-rounded'>chevron_left</span>
          </button>
        )}
      </div>
      <div className={`clock-wrap ${isClock ? 'active' : ''}`}>
        <div className='clock-container'>
          <div className='hour-marks marks'>
            {Array.from({ length: HOUR_LENGTH }, (_, i) => (
              <div key={i + 1} className={`${i % 1 === 0 ? 'hour-mark' : ''}`} style={{ transform: `rotate(${i * (360 / HOUR_LENGTH)}deg) translateY(calc(-1 * var(--marker-distance)))` }}>
                {i % 1 === 0 && <span style={{ transform: `rotate(${i * -(360 / HOUR_LENGTH)}deg)` }}>{i / 1 !== 0 ? i / 1 : '12'}</span>}
              </div>
            ))}
          </div>
          <div className='minute-marks marks'>
            {Array.from({ length: MINUTE_LENGTH }, (_, i) => (
              <div
                key={i + 1}
                className={`${i % MARK_LIMIT !== 0 ? 'minute-mark' : ''}`}
                style={{ transform: `rotate(${i * (360 / MINUTE_LENGTH)}deg) translateY(calc(-1 * var(--marker-minute-distance)))` }}
              />
            ))}
          </div>
          <div className='hour-hand hand' />
          <div className='minute-hand hand' />
          <div className='second-hand hand'>
            <div className='second-end-bar' />
          </div>
          <div className='second-center-circle' />
          <div className='small-circle' />
        </div>
      </div>
      <div className={`calendar-wrap ${!isClock ? 'active' : ''}`}>
        <div className='calendar-header'>
          <span className='date-title'>
            {curDate.year}년 {monthNames[curDate.month]}
          </span>
          {viewType === 'd' && (
            <div className='date-control'>
              <button type='button' onClick={() => onClickChangeDate('prev')}>
                <span className='material-symbols-rounded'>chevron_left</span>
              </button>
              <span role='presentation' className='today-btn' onClick={() => onClickChangeDate('today')}>
                오늘
              </span>
              <button type='button' onClick={() => onClickChangeDate('next')}>
                <span className='material-symbols-rounded'>chevron_right</span>
              </button>
            </div>
          )}
        </div>
        <div className='calendar-body'>
          <div className='calendar-week-day'>
            {weekNames.map((e, idx) => (
              <div key={e} className={`${idx === 0 ? 'sunday' : ''}`}>
                {e}
              </div>
            ))}
          </div>
          <div className='calendar-days'>
            {Array.from({ length: daysOfMonth[curDate.month] + firstDay.getDay() }, (_, i) => {
              // 공휴일 체크
              if (holidaysList.some((holiday) => holiday.day === i - firstDay.getDay() + 1)) {
                return (
                  <div key={i} className='calendar-day holiday'>
                    <span>{i - firstDay.getDay() + 1}</span>
                    <span className='holiday-name'>{holidaysList.find((holiday) => holiday.day === i - firstDay.getDay() + 1)?.smName}</span>
                  </div>
                );
              }

              // 주말 체크 및 일반 일자
              if (i >= firstDay.getDay()) {
                return (
                  <div
                    key={i}
                    className={`calendar-day ${i - firstDay.getDay() + 1 === date.getDate() && curDate.year === date.getFullYear() && curDate.month === date.getMonth() ? 'today' : ''} ${
                      i % 7 === 0 ? 'sunday' : i % 7 === 6 ? 'saturday' : ''
                    }`}
                  >
                    <span>{i - firstDay.getDay() + 1}</span>
                  </div>
                );
              }

              // 빈 일자
              return <div key={i} />;
            })}
          </div>
        </div>
        {/* 월 피커 
        <div className='month-list' ref={monthPickerRef}>
          {monthNames.map((monthName: string, index: number) => (
            <div key={monthName} role='presentation' data-month={index} onClick={() => onClickMonthChange(index)}>
              {monthName}
            </div>
          ))}
        </div>
        */}
      </div>
    </ClockCalendarRoot>
  );
};
